import React from 'react';
import { Card, List, Tag, Button, Progress, Typography, Space, Tooltip } from 'antd';
import { useGeneration } from '../../../context/GenerationContext';
import { useTheme } from '../../../context/ThemeContext';
import {
    LoadingOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ClockCircleOutlined,
    DeleteOutlined,
    ClearOutlined
} from '@ant-design/icons';

const { Text } = Typography;

const GenerationQueueList = ({ inline = false }) => {
    const { generations, clearCompleted, dismissGeneration } = useGeneration();
    const { colors } = useTheme();

    if (generations.length === 0) return null;

    // Wrapper style depends on whether we're inline (in stack) or standalone
    const wrapperStyle = inline ? {
        width: 300,
        pointerEvents: 'auto'
    } : {
        position: 'absolute',
        top: 15,
        right: 20,
        width: 300,
        zIndex: 1000,
        pointerEvents: 'none'
    };

    return (
        <div style={wrapperStyle}>
            <Card
                size="small"
                title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Space>
                            <LoadingOutlined spin style={{ color: colors.primary }} />
                            <span>Generations</span>
                            <Tag style={{ background: colors.bgBase, borderColor: colors.border }}>{generations.length}</Tag>
                        </Space>
                        {generations.some(g => ['COMPLETED', 'FAILED'].includes(g.status)) && (
                            <Tooltip title="Clear Finished">
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<ClearOutlined />}
                                    onClick={(e) => {
                                        e.stopPropagation(); // Prevent card click if any
                                        clearCompleted();
                                    }}
                                    style={{ pointerEvents: 'auto', color: colors.textSecondary }}
                                />
                            </Tooltip>
                        )}
                    </div>
                }
                styles={{ header: { background: colors.bgSurface, borderBottom: `1px solid ${colors.border}` }, body: { padding: 0 } }}
                style={{
                    background: 'rgba(22, 27, 34, 0.95)', // bgSurface with slight transparency
                    backdropFilter: 'blur(10px)',
                    border: `1px solid ${colors.border}`,
                    pointerEvents: 'auto', // Re-enable pointer events for the card itself
                    maxHeight: '400px',
                    overflowY: 'auto'
                }}
            >
                <List
                    itemLayout="horizontal"
                    dataSource={generations}
                    renderItem={item => (
                        <List.Item
                            actions={[
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    onClick={() => dismissGeneration(item.id)}
                                    style={{ color: colors.textSecondary }}
                                />
                            ]}
                            style={{ padding: '8px 12px', borderBottom: `1px solid ${colors.border}` }}
                        >
                            <List.Item.Meta
                                avatar={
                                    item.status === 'IN_QUEUE' ? <ClockCircleOutlined style={{ color: '#faad14', fontSize: 20 }} /> :
                                        item.status === 'IN_PROGRESS' ? <LoadingOutlined style={{ color: colors.primary, fontSize: 20 }} /> :
                                            item.status === 'COMPLETED' ? <CheckCircleOutlined style={{ color: colors.success, fontSize: 20 }} /> :
                                                <CloseCircleOutlined style={{ color: colors.error, fontSize: 20 }} />
                                }
                                title={
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                                        <Text ellipsis style={{ maxWidth: 140, color: colors.textPrimary }}>
                                            {item.metadata.type || 'Generation'}
                                        </Text>
                                        <Text style={{ fontSize: 11, color: colors.textSecondary }}>
                                            {item.status === 'IN_QUEUE' && `Pos: ${item.queue_position}`}
                                            {item.status === 'IN_PROGRESS' && 'Processing'}
                                            {item.status === 'COMPLETED' && 'Done'}
                                        </Text>
                                    </div>
                                }
                                description={
                                    <div style={{ marginTop: 4 }}>
                                        <Text style={{ fontSize: 11, display: 'block', color: colors.textSecondary }} ellipsis>
                                            {item.metadata.prompt}
                                        </Text>
                                        {(item.status === 'IN_PROGRESS' || item.status === 'IN_QUEUE') && (
                                            <Progress
                                                percent={item.status === 'IN_QUEUE' ? 10 : 60}
                                                status={item.status === 'IN_QUEUE' ? 'active' : 'active'}
                                                size="small"
                                                showInfo={false}
                                                strokeColor={item.status === 'IN_QUEUE' ? '#faad14' : colors.primary}
                                            />
                                        )}
                                    </div>
                                }
                            />
                        </List.Item>
                    )}
                />
            </Card>
        </div>
    );
};

export default GenerationQueueList;
